<template>
  <div v-if="orgId">
    <v-tabs
      v-model="activeTab"
      :items="tabs"
      bg-color="secondary"
      slider-color="#ffe67f"
      grow
      align-tabs="center">
      <template v-slot:tab="{ item: tab }">
        <v-tab
          :class="{ [`org-tab-${tab.name}`]: true, 'disable-click': tab.disabled }"
          :ref="tab.title"
          :key="tab.route"
          selected-class="active-tab"
          :to="{ name: tab.route, params: { orgId } }">
          {{ tab.title }}
          <v-tooltip location="top" :activator="`.org-tab-${tab.name}`" :disabled="!tab.disabled">
            <span>{{ tab.disabledTooltip }}</span>
          </v-tooltip>
        </v-tab>
      </template>

      <template v-slot:item="{ item }">
        <v-tabs-window-item :value="item.value" class="pa-4">
          <div fluid>
            <div v-if="item.disabled" class="d-flex justify-center pa-10">
              <v-alert variant="outlined" type="error">{{ item.disabledTooltip }}</v-alert>
            </div>
            <component :is="item.component" v-else></component>
          </div>
        </v-tabs-window-item>
      </template>
    </v-tabs>
  </div>
  <div v-else>
    <v-alert color="yellow-lighten-4" elevation="2" class="mx-2">
      <h4>
        <v-icon color="brown darken-0" class="mr-2">mdi-alert</v-icon>
        {{ $t('org.pages.settings.messages.invalidOrganizationData') }}
      </h4>
      <div class="mt-2">
        <small>{{ $t('org.pages.settings.messages.theRequestIsInvalid') }}</small>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { useFlag } from '@loadsmart/vue-feature-flags';

import renderMixin from '@satellite/components/mixins/renderMixin';

import LoadTypesPage from '@/modules/load_type/LoadTypesPage';
import SettingsPage from '@/modules/settings/SettingsPage';
import OrgSsoConfigForm from '@/modules/orgs/components/OrgSsoConfigForm';
import WarehouseGroupsPage from '@/modules/warehouse_groups/WarehouseGroupsPage.vue';

/**
 * Warehouse Details Page
 * @displayName Warehouse Details Page
 */
export default {
  mixins: [renderMixin],
  props: {
    orgId: {
      type: String,
      required: true
    }
  },
  setup() {
    const enableWarehouseGroupsPage = useFlag('enable-warehouse-groups-page');
    return { enableWarehouseGroupsPage };
  },
  computed: {
    tabs() {
      const allTabs = [
        {
          route: 'org.settings',
          title: this.$t('org.pages.settings.tabLabels.orgSettings'),
          component: SettingsPage,
          name: 'settings'
        },
        {
          route: 'org.loadtypes',
          title: this.$t('global.specialtyTerms.orgLoadTypes'),
          component: LoadTypesPage,
          name: 'loadtypes'
        },
        {
          route: 'org.ssoconfig',
          title: this.$t('org.pages.settings.tabLabels.ssoSetup'),
          component: OrgSsoConfigForm,
          name: 'ssoconfig',
          disabled: !this.novaCore.canUserConfigureSaml(this.$me),
          disabledTooltip: this.getSamlPermissionError()
        }
      ];

      if (this.enableWarehouseGroupsPage.enabled) {
        allTabs.push({
          route: 'org.warehouse-groups',
          title: this.$t('org.pages.settings.tabLabels.warehouseGroups'),
          component: WarehouseGroupsPage,
          name: 'warehouse-groups'
        });
      }

      return allTabs;
    }
  },
  data() {
    return { activeTab: 'settings' };
  },
  methods: {
    getSamlPermissionError() {
      if (
        this.novaCore.isUserRoleGreaterOrEqual(this.$me.role, this.novaCore.UserRole.ADMIN) &&
        this.novaCore.isUserWarehouseRestricted(this.$me)
      ) {
        return "Your user is restricted to specific warehouses. Users with restricted warehouses can't configure SSO.";
      }
      return this.novaCore.getRoleActionError();
    }
  },
  watch: {
    tab(oldTab, newTab) {
      if (oldTab !== newTab) {
        this.renderKey++;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.active-tab {
  background-color: #00576c;
}
</style>
